import { useAsync } from '@nxsys/hooks/useAsync';
import {
  connectNestPensionPublicApi,
  getCompanyDraftByTokenApi,
  getCompanyDraftBankNamesApi,
  getCompanyDraftPensionStatusPublicApi,
  preAuthorizeSmartPensionPublicApi,
  submitCompanyDraftApi,
  updateCompanyDraftByTokenApi,
} from '@nxsys/services/api/companyDraft';
import {
  ConnectNestPensionParams,
  ConnectNestPensionResponse,
  ConnectSmartPensionDraftResponse,
  ConnectSmartPensionParams,
  CreateCompanyDraftResponse,
  CreateCompanyParams,
  CreateCompanyResponse,
  GetCompanyDraftPensionStatusResponse,
} from '@nxsys/types/api/company';
import { GetBankListResponse } from '@nxsys/types/api/bank';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useGetCompanyDraftByToken = () => {
  const { value, loading, error, run } = useAsync<
    CreateCompanyDraftResponse,
    string
  >(getCompanyDraftByTokenApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useGetCompanyDraftBankNames = () => {
  const { value, loading, error, run } = useAsync<
    GetBankListResponse,
    string
  >(getCompanyDraftBankNamesApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useUpdateCompanyDraftByToken = () => {
  const { value, loading, error, run } = useAsync<
    CreateCompanyDraftResponse,
    { token: string; frontendData: Record<string, any> }
  >(({ token, frontendData }) =>
    updateCompanyDraftByTokenApi(token, { frontendData }),
  );

  const _run = (token: string, frontendData: Record<string, any>) => {
    return run({ token, frontendData });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run: _run };
};

export const useConnectNestPensionPublic = () => {
  const { value, loading, error, run } = useAsync<
    ConnectNestPensionResponse,
    ConnectNestPensionParams & { token: string }
  >(({ token, ...params }) => connectNestPensionPublicApi(token, params));

  const _run = (token: string, params: ConnectNestPensionParams) => {
    return run({ token, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run: _run };
};

export const usePreAuthorizeSmartPensionPublic = () => {
  const { value, loading, error, run } = useAsync<
    ConnectSmartPensionDraftResponse,
    ConnectSmartPensionParams & { token: string }
  >(({ token, ...params }) => preAuthorizeSmartPensionPublicApi(token, params));

  const _run = (token: string, params: ConnectSmartPensionParams) => {
    return run({ token, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run: _run };
};

export const useSubmitCompanyDraft = () => {
  const { value, loading, error, run } = useAsync<
    CreateCompanyResponse,
    CreateCompanyParams & { token: string }
  >(({ token, ...params }) => submitCompanyDraftApi(token, params as CreateCompanyParams));

  const _run = (token: string, params: CreateCompanyParams) => {
    return run({ token, ...params });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run: _run };
};

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 30000;

export const useGetCompanyDraftPensionStatusPublic = () => {
  const [status, setStatus] =
    useState<GetCompanyDraftPensionStatusResponse | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    intervalRef.current = null;
    timeoutRef.current = null;
    setPolling(false);
  }, []);

  const startPolling = useCallback(
    (token: string) => {
      setError(null);
      setPolling(true);

      const poll = async () => {
        try {
          const res = await getCompanyDraftPensionStatusPublicApi(token);
          setStatus(res);
          if (
            res.smartPension.status === 'AUTHORIZED' ||
            res.smartPension.status === 'ERROR'
          ) {
            stopPolling();
          }
        } catch (err: any) {
          setError(
            err?.response?.data?.message || 'Failed to get pension status',
          );
          stopPolling();
        }
      };

      poll();
      intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

      timeoutRef.current = setTimeout(async () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        try {
          const res = await getCompanyDraftPensionStatusPublicApi(token);
          setStatus(res);
          if (!res?.smartPension?.authorized) {
            setError('Smart Pension authorization timed out');
          }
        } catch {
          // keep last known status
        } finally {
          setPolling(false);
          timeoutRef.current = null;
        }
      }, POLL_TIMEOUT_MS);
    },
    [stopPolling],
  );

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return { status, polling, error, startPolling, stopPolling };
};
